import { useState } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import { FaLock, FaCalendarAlt, FaTicketAlt } from "react-icons/fa";
import { MdEvent } from "react-icons/md";
import { AdminLogin } from "../../api/Calling";

export default function Login({ onLogin }) {
  const [loader, setLoader] = useState(false);
  const [adminId, setAdminId] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);

    const body = {
      a_email: adminId,
      a_password: password,
    };

    try {
      const resp = await AdminLogin(body);
      const { status, token, message } = resp;

      if (status) {
        toast.success(message || "Login Successful!", { autoClose: 2000 });
        localStorage.setItem("admin_crypto", token);
        onLogin(true);
        navigate("/");
      } else {
        toast.error(message || "Invalid credentials.", { autoClose: 2000 });
        setLoader(false);
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!", { autoClose: 2000 });
      setLoader(false);
    }
  };

  return (
    <>
      {loader && <Loader />}

      <div className="min-h-screen w-full flex items-center justify-center px-4 py-6 bg-main-bg relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-main-blue/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-main-blue/5 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-main-blue/10 rounded-full blur-3xl animate-pulse delay-500" />
        </div>

        {/* Floating event icons decoration */}
        <div className="absolute top-20 left-10 text-main-blue/10 animate-bounce">
          <FaCalendarAlt className="text-6xl" />
        </div>
        <div className="absolute bottom-32 right-16 text-main-blue/10 animate-bounce delay-300">
          <FaTicketAlt className="text-5xl" />
        </div>

        {/* Main login card */}
        <div className="relative bg-main-white backdrop-blur-xl border border-main-border shadow-2xl p-8 md:p-12 rounded-3xl w-full max-w-lg">
          {/* Header section */}
          <div className="flex flex-col items-center text-center mb-8">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-main-blue rounded-2xl blur-lg opacity-30" />
              <div className="relative bg-main-blue text-main-white p-5 rounded-2xl shadow-xl">
                <MdEvent className="text-4xl" />
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-main-font mb-2">
              Crypto Hash – Mine Cloud BTC Admin
            </h1>
            <p className="text-main-subfont text-sm md:text-base font-medium">
              Manage events, attendees, and more
            </p>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-main-font flex items-center gap-2">
                <MdEvent className="text-main-blue" />
                Admin Email
              </label>
              <div className="relative group">
                <input
                  type="email"
                  value={adminId}
                  onChange={(e) => setAdminId(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-main-border rounded-xl text-sm md:text-base focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 outline-none placeholder-main-subfont bg-main-white transition-all duration-300"
                  placeholder="admin@eventtrack.com"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-main-font flex items-center gap-2">
                <FaLock className="text-main-blue" />
                Password
              </label>
              <div className="relative group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-main-border rounded-xl text-sm md:text-base focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 outline-none placeholder-main-subfont bg-main-white transition-all duration-300"
                  placeholder="Enter your password"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loader}
              className="w-full bg-main-blue hover:bg-main-blue/90 text-main-white py-4 rounded-xl font-bold text-base shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-8"
            >
              {loader ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Access Dashboard"
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-main-border">
            <p className="text-xs text-main-subfont text-center">
              © {new Date().getFullYear()} Crypto Hash – Mine Cloud BTC Admin
              Portal. All rights reserved.
            </p>
          </div>
        </div>

        {/* Additional decorative element */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-main-subfont text-sm font-medium hidden md:block">
          Secure Admin Access
        </div>
      </div>
    </>
  );
}
